import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Empty, Drawer, Segmented, Skeleton, notification } from 'antd';
import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { formatDistanceToNow, format, isToday, isYesterday, parseISO } from 'date-fns';
import { t } from '../../i18n/i18n';
import {
  getNotifications,
  markNotificationAsRead,
  markNotificationsAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
} from '../../API/services/notificationService';
import './NotificationCenter.scss';

// Utility function to replace placeholders in text with payload values
const replacePlaceholders = (text, payload) => {
  if (!text || !payload || typeof text !== 'string') return text;

  // Replace all {key} placeholders with corresponding payload values
  return text.replace(/{([^}]+)}/g, (match, key) => {
    // Return the payload value if it exists, otherwise return the original placeholder
    return payload[key] !== undefined ? payload[key] : match;
  });
};

const NotificationCenter = ({ visible, onClose, onUnreadCountChange }) => {
  const queryClient = useQueryClient();
  const observerTarget = useRef(null);
  const containerRef = useRef(null);
  const [filter, setFilter] = useState('all');

  // Helper function to detect and translate whitelabel tags with payload replacement
  const translateIfNeeded = (text, payload = null) => {
    if (!text) return '';

    // Check if text looks like a translation key (uppercase with underscores)
    const isTranslationKey = /^[A-Z][A-Z0-9_]*[A-Z0-9]$/.test(text);

    let processedText = text;

    if (isTranslationKey) {
      // Try to translate, fallback to original text if translation not found
      const translated = t(text);
      // If translation returns the same key, it means translation wasn't found
      processedText = translated === text ? text : translated;
    }

    // Apply payload replacements if payload is provided
    if (payload) {
      processedText = replacePlaceholders(processedText, payload);
    }

    return processedText;
  };

  const fetchNotifications = async ({ pageParam }) => {
    const response = await getNotifications({
      limit: 5,
      nextId: pageParam,
      read: filter,
    });
    return response;
  };

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['notifications', filter],
    queryFn: fetchNotifications,
    getNextPageParam: (lastPage) => {
      const nextId = lastPage?.pagination?.nextId;
      return nextId || undefined;
    },
    enabled: visible,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
  });

  const { data: unreadCountData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: async () => {
      const response = await getNotifications({
        limit: 1,
        read: 'unread',
      });
      return response?.pagination?.total || 0;
    },
    enabled: true,
    refetchInterval: 30000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (onUnreadCountChange && unreadCountData !== undefined) {
      onUnreadCountChange(unreadCountData);
    }
  }, [unreadCountData, onUnreadCountChange]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: containerRef.current,
        threshold: 0.1,
        rootMargin: '50px',
      }
    );

    if (observerTarget.current && visible && containerRef.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, visible, filter]);

  const handleMarkAsRead = useCallback(
    async (notification) => {
      try {
        await markNotificationsAsRead([notification]);
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
        refetch();
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    },
    [queryClient, refetch]
  );

  const allNotifications = data?.pages?.flatMap((page) => page?.collection || []) || [];

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      // Nueva implementación: llama con array vacío para marcar TODAS
      const result = await markAllNotificationsAsRead();

      // Invalidar queries para refrescar la UI
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      refetch();

      // Mostrar notificación de éxito solo si se marcó al menos una
      if (result.markedCount > 0) {
        notification.success({
          message: t('NOTIFICATIONS_MARK_ALL_SUCCESS_TITLE'),
          description: t('NOTIFICATIONS_MARK_ALL_SUCCESS_DESCRIPTION').replace(
            '{count}',
            result.markedCount
          ),
          placement: 'topRight',
          duration: 3,
        });
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      notification.error({
        message: t('NOTIFICATIONS_MARK_ALL_ERROR_TITLE'),
        description: t('NOTIFICATIONS_MARK_ALL_ERROR_DESCRIPTION'),
        placement: 'topRight',
        duration: 4,
      });
    }
  }, [queryClient, refetch]);

  const handleDeleteNotification = useCallback(
    async (notificationId, createdAt) => {
      try {
        await deleteNotification(notificationId, createdAt);
        // Invalidate both specific filter queries and general queries
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
        // Force refetch the current query
        refetch();
      } catch (error) {
        console.error('Error deleting notification:', error);
      }
    },
    [queryClient, refetch]
  );

  const handleActionClick = useCallback(
    (action, notification) => {
      if (action.action) {
        window.open(action.action, '_blank');
        handleMarkAsRead(notification);
      }
    },
    [handleMarkAsRead]
  );

  const formatNotificationDate = (dateString) => {
    const date = parseISO(dateString);

    // If it's less than 1 hour ago, show relative time
    const hoursDiff = (new Date() - date) / (1000 * 60 * 60);
    if (hoursDiff < 1) {
      return formatDistanceToNow(date, { addSuffix: true });
    }

    // If it's today, show time only
    if (isToday(date)) {
      return format(date, 'h:mm a');
    }

    // If it's yesterday, show "Yesterday at time"
    if (isYesterday(date)) {
      return `Yesterday at ${format(date, 'h:mm a')}`;
    }

    // If it's within a week, show day name and time
    const daysDiff = (new Date() - date) / (1000 * 60 * 60 * 24);
    if (daysDiff < 7) {
      return format(date, "EEEE 'at' h:mm a");
    }

    // Otherwise, show full date
    return format(date, "MMM d, yyyy 'at' h:mm a");
  };

  const renderNotification = (notification) => {
    const notificationType = ['success', 'info', 'warning', 'error'].includes(notification.type)
      ? notification.type
      : 'info';

    const renderActions = () => {
      if (!notification.actions || notification.actions.length === 0) {
        return null;
      }

      return (
        <div className="notification-center-actions">
          {notification.actions.map((action, index) => {
            const buttonType =
              action.style === 'primary'
                ? 'primary'
                : action.style === 'secondary'
                ? 'default'
                : action.style === 'default'
                ? 'default'
                : action.style === 'link'
                ? 'link'
                : action.style === 'danger'
                ? 'danger'
                : 'default';

            return (
              <Button
                key={index}
                type={buttonType}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(action, notification);
                }}
              >
                {translateIfNeeded(action.label, notification.payload)}
              </Button>
            );
          })}
        </div>
      );
    };

    const renderDescription = () => {
      const rawDesc = notification.description || notification.body || '';
      const desc = translateIfNeeded(rawDesc, notification.payload);

      return (
        <div>
          {notification.messageType === 'html' ? (
            <div dangerouslySetInnerHTML={{ __html: desc }} />
          ) : (
            <div>{desc}</div>
          )}
          {renderActions()}
        </div>
      );
    };

    return (
      <div
        key={notification.id}
        className={`notification-center-item type-${notificationType} ${
          !notification.isRead ? 'unread' : ''
        } ${notification.priority ? `priority-${notification.priority}` : ''}`}
        onClick={() => !notification.isRead && handleMarkAsRead(notification)}
      >
        <div className="notification-header">
          <div className="notification-timestamp">
            {formatNotificationDate(notification.createdAt)}
          </div>
          {notification.sender && (
            <div className="notification-sender">
              {translateIfNeeded(notification.sender, notification.payload)}
            </div>
          )}
          {notification.category && (
            <div className="notification-category">{t(notification.category)}</div>
          )}
        </div>
        <OverlayTrigger
          placement="left"
          overlay={
            <Tooltip id={`delete-tooltip-${notification.id}`}>
              {t('NOTIFICATIONS_DELETE') || 'Delete notification'}
            </Tooltip>
          }
        >
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            className="notification-delete-button"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteNotification(notification.id, notification.createdAt);
            }}
          />
        </OverlayTrigger>
        <div className="notification-center-item-content">
          <Alert
            message={translateIfNeeded(
              notification.title || notification.message || 'Notification',
              notification.payload
            )}
            description={renderDescription()}
            type={notificationType}
            showIcon
            icon={notification.icon}
            closable={false}
          />
        </div>
      </div>
    );
  };

  const unreadCount = allNotifications.filter((notification) => !notification.isRead).length;

  const renderSkeletonNotification = () => (
    <div className="notification-skeleton-item">
      <Skeleton active paragraph={{ rows: 2 }} />
    </div>
  );

  return (
    <Drawer
      title={
        <div className="notification-center-header">
          <div className="notification-center-title">
            <span>{t('NOTIFICATIONS_CENTER_TITLE') || 'Notifications'}</span>
          </div>
          {unreadCount > 0 && (
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="mark-all-read-tooltip">
                  {t('NOTIFICATIONS_MARK_ALL_READ') || 'Mark all as read'}
                </Tooltip>
              }
            >
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={handleMarkAllAsRead}
                className="notification-center-mark-all-read"
              />
            </OverlayTrigger>
          )}
        </div>
      }
      placement="right"
      width={400}
      onClose={onClose}
      open={visible}
      className="notification-center-drawer"
    >
      <div className="notification-center-filter">
        <Segmented
          options={[
            { label: t('NOTIFICATIONS_FILTER_ALL') || 'All', value: 'all' },
            { label: t('NOTIFICATIONS_FILTER_UNREAD') || 'Unread', value: 'unread' },
          ]}
          value={filter}
          onChange={setFilter}
          block
        />
      </div>
      <div className="notification-center-container" ref={containerRef}>
        {status === 'loading' ? (
          <div className="notification-center-list">
            {Array.from({ length: 3 }, (_, index) => (
              <div key={`skeleton-${index}`}>{renderSkeletonNotification()}</div>
            ))}
          </div>
        ) : status === 'error' ? (
          <div className="notification-center-error">
            <Alert
              message={t('NOTIFICATIONS_ERROR_TITLE') || 'Error loading notifications'}
              description={
                t('NOTIFICATIONS_ERROR_DESCRIPTION') ||
                'Unable to load notifications. Please try again later.'
              }
              type="error"
              showIcon
            />
          </div>
        ) : allNotifications.length === 0 ? (
          <div className="notification-center-empty">
            <Empty
              description={
                <div className="notification-empty-content">
                  <div className="notification-empty-description">
                    {t('NOTIFICATIONS_EMPTY') || 'No notifications'}
                  </div>
                </div>
              }
            />
          </div>
        ) : (
          <>
            <div className="notification-center-list">
              {allNotifications.map(renderNotification)}
            </div>

            {isFetchingNextPage && (
              <div className="notification-skeleton-container">{renderSkeletonNotification()}</div>
            )}
            <div ref={observerTarget} className="notification-center-observer" />
          </>
        )}
      </div>
    </Drawer>
  );
};

export default NotificationCenter;
